package puzzlekey

import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min

/** Side of the square colour image the Scan model takes, in pixels (ADR-0009). */
const val MODEL_SIZE = 224

/** Share of the guide, centred, that the model sees: enough margin that the frame (pink or blue) stays out. */
const val INNER_SHARE = 0.7

private val IMAGENET_MEAN = doubleArrayOf(0.485, 0.456, 0.406)
private val IMAGENET_STD = doubleArrayOf(0.229, 0.224, 0.225)

/** The centred [share] of [guide], rounded the way the T07 prototype rounded it. */
fun innerCrop(guide: Rect, share: Double = INNER_SHARE): Rect {
    val dx = (guide.w * (1 - share) / 2).toInt()
    val dy = (guide.h * (1 - share) / 2).toInt()
    return Rect(guide.x + dx, guide.y + dy, guide.w - 2 * dx, guide.h - 2 * dy)
}

/**
 * The model's input tensor for a Scan (ADR-0009 §1): the centred [share] of [guide], squeezed to
 * [MODEL_SIZE]² whatever its aspect, in colour, scaled by the ImageNet mean/std. Planar layout
 * (all R, then all G, then all B), row by row: ONNX's `[1, 3, 224, 224]`.
 *
 * The resize is PIL's antialiased bilinear filter, as the prototype's torchvision `Resize` used, so the
 * site and `seal` see what the prototype saw. Arithmetic is in Double and stored as Float at the end,
 * which makes the result the same on the JVM and in JS (where Float arithmetic runs in double precision).
 */
fun modelInput(frame: Rgba, guide: Rect, share: Double = INNER_SHARE): FloatArray {
    require(guide.x >= 0 && guide.y >= 0 && guide.x + guide.w <= frame.width && guide.y + guide.h <= frame.height) {
        "guide $guide is outside the ${frame.width}×${frame.height} frame"
    }
    val crop = innerCrop(guide, share)
    val xs = filterTaps(crop.w, MODEL_SIZE)
    val ys = filterTaps(crop.h, MODEL_SIZE)

    // Horizontal pass: crop.h rows × MODEL_SIZE columns × 3 channels, 0..255.
    val wide = DoubleArray(crop.h * MODEL_SIZE * 3)
    for (y in 0 until crop.h) {
        val row = (crop.y + y) * frame.width + crop.x
        for (ox in 0 until MODEL_SIZE) {
            var r = 0.0; var g = 0.0; var b = 0.0
            val first = xs.first[ox]
            val weights = xs.weights[ox]
            for (k in weights.indices) {
                val i = (row + first + k) * 4
                val w = weights[k]
                r += w * (frame.bytes[i].toInt() and 0xFF)
                g += w * (frame.bytes[i + 1].toInt() and 0xFF)
                b += w * (frame.bytes[i + 2].toInt() and 0xFF)
            }
            val o = (y * MODEL_SIZE + ox) * 3
            wide[o] = r; wide[o + 1] = g; wide[o + 2] = b
        }
    }

    // Vertical pass, then ImageNet units.
    val plane = MODEL_SIZE * MODEL_SIZE
    val out = FloatArray(3 * plane)
    for (oy in 0 until MODEL_SIZE) {
        val first = ys.first[oy]
        val weights = ys.weights[oy]
        for (ox in 0 until MODEL_SIZE) for (c in 0..2) {
            var v = 0.0
            for (k in weights.indices) v += weights[k] * wide[((first + k) * MODEL_SIZE + ox) * 3 + c]
            out[c * plane + oy * MODEL_SIZE + ox] = ((v / 255 - IMAGENET_MEAN[c]) / IMAGENET_STD[c]).toFloat()
        }
    }
    return out
}

/** For each output pixel, the first source pixel it reads and the weights of the source pixels from there on. */
private class Taps(val first: IntArray, val weights: Array<DoubleArray>)

/**
 * Coefficients of PIL's BILINEAR resample (`ImagingResample`): a triangle filter widened by the
 * downscale factor, so every source pixel contributes (antialiasing), normalised per output pixel.
 */
private fun filterTaps(inSize: Int, outSize: Int): Taps {
    val scale = inSize.toDouble() / outSize
    val filterScale = max(scale, 1.0)
    val support = filterScale // the triangle's support is 1, widened by the downscale factor
    val first = IntArray(outSize)
    val weights = Array(outSize) { o ->
        val centre = (o + 0.5) * scale
        val lo = max((centre - support + 0.5).toInt(), 0)
        val hi = min((centre + support + 0.5).toInt(), inSize)
        first[o] = lo
        val w = DoubleArray(hi - lo) { k -> max(0.0, 1 - abs((lo + k - centre + 0.5) / filterScale)) }
        val sum = w.sum()
        DoubleArray(w.size) { w[it] / sum }
    }
    return Taps(first, weights)
}
